<template>
  <!-- Converter Box -->
  <div class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden relative">
    <!-- Hidden File Input (Moved outside to ensure it's always accessible) -->
    <input type="file" ref="fileInput" class="hidden" multiple accept="image/*,.heic,.heif" @change="handleFileSelect" />

    <!-- Dropzone -->
    <div v-if="files.length === 0" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop" class="p-10 md:p-10">
      <div @click="triggerFileInput" :class="['border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors', isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-400 hover:bg-gray-50']">
        <div class="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-2">
          <UploadCloud class="w-8 h-8 text-blue-600" />
        </div>
        <span class="text-xl font-semibold text-slate-900 mb-5">Drop your images here</span>
        <p class="text-gray-500 text-xs font-medium mb-5 mt-1">PNG, JPG, WebP, GIF up to 50MB</p>
        <div class="inline-flex px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold text-sm shadow-sm hover:bg-blue-700 transition-colors">
          Select Files
        </div>
      </div>
          <!-- Privacy Shield -->
      <div class="mt-8 ml-3 bg-gray-50/30 border-t border-gray-50 flex items-center justify-start gap-2">
        <ShieldCheck class="w-4 h-4 text-blue-600" />
        <p class="text-[13px] font-semibold text-blue-600">
          *Your privacy is protected! No data is transmitted or stored.
        </p>
      </div>
    </div>

    <!-- File List -->
    <div v-else class="p-4 md:p-4">
      <div class="flex items-center justify-between mb-1 px-4">
          <div class="flex items-center gap-3">
              <h3 class="text-sm font-bold text-slate-900 uppercase tracking-tight">Files Queue</h3>
              <div class="flex items-center gap-2">
                <span class="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">{{ files.length }}</span>
                <span class="text-[11px] font-semibold text-slate-400">• {{ totalQueueSize }}</span>
              </div>
          </div>
      </div>

      <div @scroll="handleListScroll" class="space-y-1.5 max-h-[459px] overflow-y-auto p-3 px-4 rounded-2xl custom-scrollbar relative">
        <div v-for="(file, index) in files" :key="index" class="bg-white rounded-xl p-2 px-4 flex flex-col md:flex-row md:items-center justify-between gap-3 border border-gray-200 hover:shadow-sm transition-all group/item">
        <div class="flex items-center gap-3 flex-1 overflow-hidden">
          <!-- Thumbnail/Preview -->
          <div @click="openPreview(file.preview)" class="w-14 h-12 rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center p-1 cursor-pointer group/thumb relative">
            <img v-if="file.preview" :src="file.preview" class="w-full h-full object-contain transition-transform duration-300" />
            <span v-else class="text-gray-300 font-bold text-[10px]">{{ getExt(file.name) }}</span>
            <div v-if="file.preview" class="absolute inset-0 bg-black/20 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
              <Eye class="w-5 h-5 text-white" />
            </div>
          </div>
          <div class="min-w-0 flex-1">
            <h4 class="font-bold text-slate-800 truncate text-xs mb-0.5 max-w-[120px] md:max-w-[320px]" :title="file.name">{{ file.name }}</h4>
            <div class="flex items-center gap-2">
              <span class="text-[10px] text-blue-600 font-bold uppercase">{{ formatSize(file.size) }}</span>
              <span class="text-[10px] text-slate-300 font-semibold">•</span>
              <span class="text-[10px] text-slate-400 font-semibold uppercase">{{ getExt(file.name) }}</span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2.5">
            <span class="text-[12px] font-semibold text-slate-600">Convert To</span>
            
            <!-- Custom Format Selector -->
            <div class="relative min-w-[90px]">
              <button @click.stop="toggleDropdown(index, $event)" :class="['w-full border rounded-md px-4 py-2 text-xs font-bold flex items-center justify-between transition-all outline-none shadow-sm', file.targetFormat ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' : 'bg-white border-gray-200 text-slate-600 hover:border-blue-500 hover:text-blue-600']">
                <span class="tracking-tight">{{ file.targetFormat || 'Select' }}</span>
                <ChevronDown class="w-4 h-4 opacity-70" :class="{ 'rotate-180': activeDropdown === index }"/>
              </button>

              <!-- Dropdown Menu -->
              <teleport to="body">
                <transition name="dropdown">
                    <div v-if="activeDropdown === index" :style="dropdownStyle" :class="['bg-white border border-blue-100 rounded-md shadow-2xl z-[9999] p-2.5 grid grid-cols-3 gap-2 ring-1 ring-blue-50', dropdownIsUpward ? 'origin-bottom' : 'origin-top']">
                    <button v-for="format in getFilteredFormats(getExt(file.name))" :key="format" @click="selectFormat(file, format)" class="text-center px-1 py-3 text-[11px] font-bold rounded-md bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all border border-blue-100 hover:border-blue-400 shadow-sm uppercase tracking-tight">
                        {{ format }}
                    </button>
                    </div>
                </transition>
              </teleport>
            </div>
          </div>
            <!-- Individual Action -->
            <div class="flex items-center">
              <!-- Loading Spinner -->
              <div v-if="file.isConverting" class="w-9 h-9 flex items-center justify-center">
                <svg class="animate-spin h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>

              <!-- Convert Button -->
              <button v-else-if="!file.convertedUrl" 
                @click="individualConvert(file)" 
                @mouseenter="showTooltip($event, file.targetFormat ? 'Convert Now' : 'Select Format First')" 
                @mouseleave="hideTooltip"
                :disabled="!file.targetFormat" 
                :class="['w-8 h-8 rounded-md flex items-center justify-center transition-all group/btn shadow-sm', file.targetFormat ? 'bg-slate-900 text-white hover:bg-black hover:shadow-md cursor-pointer' : 'bg-slate-900 text-white cursor-default opacity-30']"
              >
                <RefreshCw class="w-3.5 h-3.5 transition-transform duration-500" :class="{ 'group-hover/btn:rotate-180': file.targetFormat }" />
              </button>

              <!-- Download Button -->
              <a v-else-if="file.convertedUrl" 
                :href="file.convertedUrl" 
                :download="file.downloadName" 
                @mouseenter="showTooltip($event, 'Download')" 
                @mouseleave="hideTooltip"
                class="w-8 h-8 rounded-md bg-green-600 flex items-center justify-center text-white transition-all hover:bg-green-700 hover:shadow-md shadow-sm"
              >
                <Download class="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
          
          <button @click="removeFile(index)" 
            @mouseenter="showTooltip($event, 'Remove')" 
            @mouseleave="hideTooltip"
            class="w-8 h-8 rounded-md bg-red-500 flex items-center justify-center text-white transition-all hover:bg-red-600 hover:shadow-md shadow-sm"
          >
            <Trash2 class="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    
      <!-- Bulk Actions -->
      <div v-if="files.length > 0" class="mt-2 bg-gray-50/50 rounded-md p-2 flex flex-col md:flex-row md:items-center justify-between gap-6 border border-gray-100">
        <div class="flex items-center gap-6">
            <div class="flex items-center gap-4">
                <button @click="triggerFileInput" class="bg-blue-50 border border-blue-100 hover:border-blue-200 hover:bg-blue-100 px-4 py-2 rounded-md text-xs font-bold text-blue-600 transition-all flex items-center gap-2 shadow-sm">
                    <Plus class="w-4 h-4" />
                    Add More
                </button>
                <button @click="clearAll" class="bg-red-50 border border-red-100 hover:border-red-200 hover:bg-red-100 px-4 py-2 rounded-md text-xs font-bold text-red-600 transition-all flex items-center gap-2 shadow-sm">
                    <RotateCcw class="w-3.5 h-3.5" />
                    Start Over
                </button>
            </div>
            
            <div v-if="files.length > 1" class="h-8 w-px bg-gray-200 hidden md:block"></div>

            <div v-if="files.length > 1" class="flex items-center gap-3">
                <p class="text-slate-400 text-[10px] font-bold uppercase tracking-wider">Convert All To</p>
                <div class="relative min-w-[110px]">
                    <button @click.stop="toggleBulkDropdown($event)" :class="['w-full border rounded-md px-4 py-2 text-xs font-bold flex items-center justify-between transition-all outline-none', bulkFormat ? 'bg-blue-50 border-blue-200 text-blue-700 hover:border-blue-100' : 'bg-white border-gray-200 text-slate-600 hover:border-blue-500 hover:text-blue-600 shadow-sm']">
                        <span class="tracking-tight">{{ bulkFormat || 'Select' }}</span>
                        <ChevronDown class="w-3.5 h-3.5 opacity-70 transition-transform duration-200" :class="{ 'rotate-180': isBulkDropdownOpen }"/>
                    </button>

                    <!-- Dropdown Menu -->
                    <teleport to="body">
                        <transition name="dropdown">
                            <div v-if="isBulkDropdownOpen" :style="dropdownStyle" class="bg-white border border-blue-100 rounded-md shadow-2xl z-[9999] p-2.5 grid grid-cols-3 gap-2 ring-1 ring-blue-50">
                                <button v-for="format in commonFormats" :key="format" @click="selectBulkFormat(format)" class="text-center px-1 py-3 text-[11px] font-bold rounded-md bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all border border-blue-100 hover:border-blue-400 shadow-sm uppercase tracking-tight">
                                    {{ format }}
                                </button>
                            </div>
                        </transition>
                    </teleport>
                </div>
            </div>
        </div>
        <button v-if="!isBatchConverted" @click="processFiles" :disabled="!isReadyToConvert" :class="['px-4 py-2.5 rounded-md font-semibold text-sm transition-all flex items-center gap-3 w-full md:w-auto justify-center group shadow-lg', isReadyToConvert ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/20' : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none']">
          <span>Convert All Files</span>
          <RefreshCw class="w-4 h-4 transition-transform duration-500" :class="{ 'group-hover:rotate-180': isReadyToConvert }" />
        </button>
        <button v-else @click="handleFinalDownload" class="bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-md font-semibold text-sm transition-all shadow-lg shadow-green-500/20 flex items-center gap-3 w-full md:w-auto justify-center">
          <span>{{ results.length > 1 ? 'Download ZIP' : 'Download Image' }}</span>
          <Download class="w-4 h-4" />
        </button>
      </div>
    </div>
    <!-- Processing Overlay -->
    <div v-if="isProcessing" class="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
        <div class="max-w-xs w-full text-center">
            <Loader2 class="w-12 h-12 text-blue-500 animate-spin mx-auto mb-6" />
            <h3 class="text-xl font-bold text-white mb-2 uppercase tracking-tight">Processing...</h3>
            <div class="w-full bg-white/10 h-1.5 rounded-full overflow-hidden mb-6">
                <div class="bg-blue-500 h-full transition-all duration-300" :style="{ width: progress + '%' }"></div>
            </div>
            <p class="text-slate-400 text-sm font-medium italic">{{ processingMessage }}</p>
        </div>
    </div>


    <!-- Hidden Canvas (No longer needed globally, but kept for script reference if any) -->
    <div class="hidden"></div>
    <!-- Image Preview Modal -->
    <teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="isPreviewOpen" class="fixed inset-0 z-[10000] flex items-center justify-center p-4 md:p-8">
          <!-- Backdrop -->
          <div class="absolute inset-0 bg-slate-950/80 backdrop-blur-sm transition-opacity duration-300" @click="closePreview"></div>
          
          <!-- Content Container -->
          <div class="relative max-w-5xl w-full max-h-full flex flex-col items-center justify-center transform transition-all duration-300 ease-out scale-100">

            <!-- Image Wrapper -->
            <div class="bg-white/5 p-2 rounded-2xl border border-white/10 shadow-2xl relative overflow-hidden group/modal">
              <!-- Close Button Inside -->
              <button @click="closePreview" class="absolute top-4 right-4 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-all backdrop-blur-md border border-white/10 z-[10002]" title="Close Preview">
                <X class="w-5 h-5" />
              </button>

              <img :src="previewImageSrc" class="max-w-full max-h-[80vh] md:max-h-[85vh] object-contain rounded-lg shadow-inner shadow-black/20" alt="Preview"/>

              <!-- Subtle Info Overlay -->
              <div class="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/40 backdrop-blur-md border border-white/10 rounded-full opacity-0 group-hover/modal:opacity-100 transition-opacity duration-300 text-white text-xs font-medium">
                Click background to close
              </div>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- Global Drag and Drop Overlay (Decent & Minimal) -->
    <teleport to="body">
      <transition 
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 scale-[0.98]"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-[0.98]"
      >
        <div v-show="isGlobalDragging" class="fixed inset-0 z-[100005] pointer-events-none p-4 md:p-8 flex items-center justify-center">
            <!-- Subtle Tint -->
            <div class="absolute inset-0 bg-blue-600/[0.02] backdrop-blur-[1px]"></div>
            
            <!-- Viewport Border -->
            <div class="absolute inset-4 md:inset-8 border-2 border-dashed border-blue-400/20 rounded-[2rem]"></div>
            
            <!-- Minimal Floating Pill -->
            <div class="relative bg-white shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-blue-100/50 px-6 py-3 rounded-full flex items-center gap-3 animate-pulse">
                <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center">
                    <UploadCloud class="w-4 h-4" />
                </div>
                <span class="text-slate-800 font-bold text-sm tracking-tight">Drop images to upload</span>
            </div>
        </div>
      </transition>
    </teleport>

    <!-- Professional Global Teleported Tooltip -->
    <teleport to="body">
      <transition 
        enter-active-class="transition duration-400 ease-[cubic-bezier(0.23,1,0.32,1)]"
        enter-from-class="opacity-0 translate-y-1.5 scale-95"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="transition duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div v-show="tooltipState.show" 
          :style="{ left: tooltipState.x + 'px', top: tooltipState.y + 'px' }" 
          class="fixed z-[99999] pointer-events-none -translate-x-1/2 mt-3 transform-gpu will-change-transform"
        >
          <div class="bg-[#111010f2] backdrop-blur-md text-white px-3 py-1.5 rounded-xl text-[11px] font-bold shadow-[0_8px_30px_rgb(0,0,0,0.4)] border border-white/10 whitespace-nowrap relative">
            {{ tooltipState.text }}
            <!-- Arrow on Top -->
            <div class="absolute bottom-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-b-[#111010f2]"></div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, watch, computed } from 'vue';
import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import heic2any from 'heic2any';
import { 
  UploadCloud, 
  ChevronDown, 
  RefreshCw, 
  Download, 
  Trash2, 
  Plus, 
  Loader2,
  RotateCcw,
  X,
  Eye,
  ShieldCheck
} from 'lucide-vue-next';

const files = ref([]);
const results = ref([]);

const totalQueueSize = computed(() => {
  const totalBytes = files.value.reduce((sum, file) => sum + (file.size || 0), 0);
  return formatSize(totalBytes);
});

const isReadyToConvert = computed(() => {
    return files.value.length > 0 && files.value.every(f => f.targetFormat);
});

const isDownloadingZip = ref(false);
const isDragging = ref(false);
const fileInput = ref(null);
const isProcessing = ref(false);
const progress = ref(0);
const processingMessage = ref('Loading files...');
const isBatchConverted = ref(false);
const bulkFormat = ref('');
const activeDropdown = ref(null);
const activeDropdownEl = ref(null);
const isBulkDropdownOpen = ref(false);
const dropdownStyle = ref({});
const dropdownIsUpward = ref(false);
const isGlobalDragging = ref(false);
let dragCounter = 0;

// Global Tooltip State
const tooltipState = reactive({
  show: false,
  text: '',
  x: 0,
  y: 0
});

const showTooltip = (e, text) => {
  const rect = e.currentTarget.getBoundingClientRect();
  tooltipState.text = text;
  tooltipState.x = rect.left + rect.width / 2;
  tooltipState.y = rect.bottom;
  tooltipState.show = true;
};

const hideTooltip = () => {
  tooltipState.show = false;
};

const allFormats = ['PNG', 'JPG', 'WEBP', 'PDF', 'AVIF', 'BMP', 'TIFF', 'ICO'];
const commonFormats = ['PNG', 'JPG', 'WEBP', 'PDF', 'AVIF', 'BMP', 'TIFF', 'ICO'];

// Image Preview State
const isPreviewOpen = ref(false);
const previewImageSrc = ref('');

const openPreview = (src) => {
  if (!src) return;
  previewImageSrc.value = src;
  isPreviewOpen.value = true;
};

const closePreview = () => {
  isPreviewOpen.value = false;
};

const triggerFileInput = () => {
  fileInput.value.click();
};

const handleFileSelect = (e) => {
  addFiles(Array.from(e.target.files));
};

const handleDrop = (e) => {
  isDragging.value = false;
  addFiles(Array.from(e.dataTransfer.files));
};

const addFiles = async (newFiles) => {
  for (const file of newFiles) {
    const isImage = file.type.startsWith('image/');
    const isHeic = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
    
    if (isImage || isHeic) {
       let previewFile = file;
       
       // Handle HEIC/HEIF preview conversion
       if (isHeic) {
         try {
           const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.3 });
           previewFile = Array.isArray(converted) ? converted[0] : converted;
         } catch (e) {
           console.error('HEIC Preview Error:', e);
         }
       }

       const preview = URL.createObjectURL(previewFile);
       
       files.value.push({
         name: file.name,
         size: file.size,
         file: file,
         preview: preview,
         targetFormat: '', // Default to empty
         isConverting: false
       });
    }
  }
};

const removeFile = (index) => {
  hideTooltip(); // Fix ghost tooltip
  const file = files.value[index];
  if (file.preview) URL.revokeObjectURL(file.preview);
  files.value.splice(index, 1);
};

const clearAll = () => {
  hideTooltip(); // Fix ghost tooltip
  files.value.forEach(f => {
    if (f.preview) URL.revokeObjectURL(f.preview);
  });
  files.value = [];
  results.value = [];
};

const getExt = (filename) => {
  return filename ? filename.split('.').pop().toUpperCase() : '';
};

// Reset conversion state when files change
watch(files, () => {
    isBatchConverted.value = false;
    results.value = [];
}, { deep: true });

const getFilteredFormats = (sourceExt) => {
  return allFormats;
};

const formatSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const applyBulkFormat = () => {
    if (!bulkFormat.value) return;
    files.value.forEach(file => {
        file.targetFormat = bulkFormat.value;
        file.convertedUrl = null; // Reset generated link
    });
};

const toggleDropdown = (index, event) => {
  if (activeDropdown.value === index) {
    activeDropdown.value = null;
    activeDropdownEl.value = null;
  } else {
    activeDropdown.value = index;
    activeDropdownEl.value = event.currentTarget;
    isBulkDropdownOpen.value = false;
    updateDropdownPosition();
  }
};

const updateDropdownPosition = () => {
    if (activeDropdown.value === null || !activeDropdownEl.value) return;

    const rect = activeDropdownEl.value.getBoundingClientRect();
    const dropdownHeight = 120; // Improved height for grid
    const dropdownWidth = 210;
    const spaceBelow = window.innerHeight - rect.bottom;
    
    dropdownIsUpward.value = spaceBelow < dropdownHeight;

    // Calculate left but keep on screen
    const left = Math.max(16, Math.min(rect.right - dropdownWidth, window.innerWidth - dropdownWidth - 16));

    if (dropdownIsUpward.value) {
        dropdownStyle.value = {
            position: 'fixed',
            bottom: `${window.innerHeight - rect.top + 8}px`,
            left: `${left}px`,
            width: `${dropdownWidth}px`
        };
    } else {
        dropdownStyle.value = {
            position: 'fixed',
            top: `${rect.bottom + 8}px`,
            left: `${left}px`,
            width: `${dropdownWidth}px`
        };
    }
};

const handleListScroll = () => {
    if (activeDropdown.value !== null) {
        updateDropdownPosition();
    }
};

const selectFormat = (file, format) => {
  file.targetFormat = format;
  file.convertedUrl = null; // Reset generated link
  activeDropdown.value = null;
};

const toggleBulkDropdown = (event) => {
  if (isBulkDropdownOpen.value) {
    isBulkDropdownOpen.value = false;
  } else {
    isBulkDropdownOpen.value = true;
    activeDropdown.value = null;

    const rect = event.currentTarget.getBoundingClientRect();
    const dropdownWidth = 210;
    const left = Math.max(16, Math.min(rect.left, window.innerWidth - dropdownWidth - 16));

    dropdownStyle.value = {
      position: 'fixed',
      bottom: `${window.innerHeight - rect.top + 8}px`,
      left: `${left}px`,
      width: `${dropdownWidth}px`
    };
  }
};

const selectBulkFormat = (format) => {
  bulkFormat.value = format;
  applyBulkFormat();
  isBulkDropdownOpen.value = false;
};

// Close dropdowns on click, scroll, or resize
const closeDropdowns = () => {
  activeDropdown.value = null;
  isBulkDropdownOpen.value = false;
};

const handleGlobalDragEnter = (e) => {
  e.preventDefault();
  dragCounter++;
  if (dragCounter === 1) isGlobalDragging.value = true;
};

const handleGlobalDragLeave = (e) => {
  e.preventDefault();
  dragCounter--;
  if (dragCounter <= 0) {
    dragCounter = 0;
    isGlobalDragging.value = false;
  }
};

const handleGlobalDrop = (e) => {
  e.preventDefault();
  dragCounter = 0;
  isGlobalDragging.value = false;
  if (e.dataTransfer.files) {
    addFiles(Array.from(e.dataTransfer.files));
  }
};

if (typeof window !== 'undefined') {
  onMounted(() => {
    window.addEventListener('dragenter', handleGlobalDragEnter);
    window.addEventListener('dragleave', handleGlobalDragLeave);
    window.addEventListener('dragover', (e) => e.preventDefault());
    window.addEventListener('drop', handleGlobalDrop);
    window.addEventListener('click', closeDropdowns);
    window.addEventListener('scroll', closeDropdowns, { capture: true, passive: true });
    window.addEventListener('resize', closeDropdowns);
  });

  onUnmounted(() => {
    hideTooltip();
    window.removeEventListener('dragenter', handleGlobalDragEnter);
    window.removeEventListener('dragleave', handleGlobalDragLeave);
    window.removeEventListener('dragover', (e) => e.preventDefault());
    window.removeEventListener('drop', handleGlobalDrop);
    window.removeEventListener('click', closeDropdowns);
    window.removeEventListener('scroll', closeDropdowns, { capture: true });
    window.removeEventListener('resize', closeDropdowns);
    clearAll();
  });
}

// Lock scroll when modal is open and prevent layout shift
watch([results, isPreviewOpen], ([newResults, newIsPreviewOpen]) => {
    if (typeof document !== 'undefined') {
        const shouldLock = newResults.length > 0 || (typeof isProcessing !== 'undefined' && isProcessing.value) || newIsPreviewOpen;
        
        if (shouldLock) {
            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.paddingRight = `${scrollBarWidth}px`;
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.paddingRight = '';
            document.body.style.overflow = '';
        }
    }
}, { deep: true });

// Hide/Show Blade hero section based on file count
watch(() => files.value.length, (count) => {
    if (typeof document !== 'undefined') {
        const hero = document.getElementById('hero-section');
        if (hero) {
            hero.style.display = count > 0 ? 'none' : 'block';
        }
    }
}, { immediate: true });

const processFiles = async () => {
    if (files.value.length === 0) return;
    
    isProcessing.value = true;
    progress.value = 0;
    processingMessage.value = 'Preparing files...';
    results.value = [];

    const totalFiles = files.value.length;
    let completedCount = 0;

    try {
        processingMessage.value = 'Converting images...';
        const conversionPromises = files.value.map(async (fileObj) => {
            fileObj.isConverting = true;
            try {
                const result = await convertImage(fileObj);
                fileObj.convertedUrl = result.url;
                fileObj.downloadName = result.downloadName;
                completedCount++;
                progress.value = Math.round((completedCount / totalFiles) * 100);
                return result;
            } finally {
                fileObj.isConverting = false;
            }
        });

        results.value = await Promise.all(conversionPromises);
        isBatchConverted.value = true;
        
        progress.value = 100;
        processingMessage.value = 'Success!';
    } catch (err) {
        console.error('Bulk conversion error:', err);
        processingMessage.value = 'Conversion failed';
    } finally {
        setTimeout(() => {
            isProcessing.value = false;
        }, 800);
    }
};

const handleFinalDownload = async () => {
    if (results.value.length === 0) return;

    if (results.value.length === 1) {
        // Direct download single file
        const res = results.value[0];
        const link = document.createElement('a');
        link.href = res.url;
        link.download = res.downloadName;
        link.click();
    } else {
        // ZIP download multiple files
        await downloadAllAsZip();
    }
};

const individualConvert = async (fileObj) => {
    fileObj.isConverting = true;
    try {
        const result = await convertImage(fileObj);
        fileObj.convertedUrl = result.url;
        fileObj.downloadName = result.downloadName;
    } catch (err) {
        console.error('Individual conversion error:', err);
    } finally {
        fileObj.isConverting = false;
    }
};

const convertAndDownload = async (fileObj) => {
    // This function can be retired or kept for internal use if needed
    await individualConvert(fileObj);
    if (fileObj.convertedUrl) {
        const link = document.createElement('a');
        link.href = fileObj.convertedUrl;
        link.download = fileObj.downloadName;
        link.click();
    }
};

const downloadAllAsZip = async () => {
    if (results.value.length === 0) return;
    
    isDownloadingZip.value = true;
    const zip = new JSZip();
    
    try {
        for (const res of results.value) {
            const response = await fetch(res.url);
            const blob = await response.blob();
            zip.file(res.downloadName, blob);
        }
        
        const content = await zip.generateAsync({ type: 'blob' });
        const zipUrl = URL.createObjectURL(content);
        
        const link = document.createElement('a');
        link.href = zipUrl;
        link.download = `converted_images_${Date.now()}.zip`;
        link.click();
        
        URL.revokeObjectURL(zipUrl);
    } catch (err) {
        console.error('ZIP Error:', err);
    } finally {
        isDownloadingZip.value = false;
    }
};

const convertImage = (fileObj) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = fileObj.preview;
        img.onload = () => {
            const canvas = document.createElement('canvas'); // Local canvas for speed
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);

            // Mime types
            const mimes = {
                'PNG': 'image/png',
                'JPG': 'image/jpeg',
                'WEBP': 'image/webp',
                'GIF': 'image/gif',
                'AVIF': 'image/avif',
                'BMP': 'image/bmp',
                'TIFF': 'image/tiff',
                'ICO': 'image/x-icon'
            };

            const targetMime = mimes[fileObj.targetFormat] || 'image/png';
            const quality = fileObj.targetFormat === 'JPG' ? 0.9 : 1;

            if (fileObj.targetFormat === 'PDF') {
                const pdf = new jsPDF({
                    orientation: img.width > img.height ? 'l' : 'p',
                    unit: 'px',
                    format: [img.width, img.height]
                });
                pdf.addImage(canvas.toDataURL('image/jpeg', 1.0), 'JPEG', 0, 0, img.width, img.height);
                const blob = pdf.output('blob');
                const url = URL.createObjectURL(blob);
                const nameWithoutExt = fileObj.name.split('.').slice(0, -1).join('.');
                
                resolve({
                    name: fileObj.name,
                    downloadName: `${nameWithoutExt}.pdf`,
                    format: 'PDF',
                    url: url,
                    preview: fileObj.preview
                });
                return;
            }
            
            canvas.toBlob((blob) => {
                if (!blob) return reject('Blob failed');
                const url = URL.createObjectURL(blob);
                const nameWithoutExt = fileObj.name.split('.').slice(0, -1).join('.');
                
                resolve({
                    name: fileObj.name,
                    downloadName: `${nameWithoutExt}.${fileObj.targetFormat.toLowerCase()}`,
                    format: fileObj.targetFormat,
                    url: url,
                    preview: fileObj.preview
                });
            }, targetMime, quality);
        };
        img.onerror = reject;
    });
};

onUnmounted(() => {
    clearAll();
    if (typeof window !== 'undefined') {
        window.removeEventListener('click', closeDropdowns);
        window.removeEventListener('scroll', closeDropdowns, { capture: true });
        window.removeEventListener('resize', closeDropdowns);
    }
});
</script>

<style scoped>
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  margin: 8px 0;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
  border: 1px solid transparent;
  background-clip: content-box;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
  border: 0;
}

.inner-shadow-sm {
    box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.02);
}

/* Dropdown Animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease-out;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
